import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './guards/auth.guard';
import { AutoLoginGuard } from './guards/auto-login.guard';
import { AutoLogoutGuard } from './guards/auto-logout.guard';
import { CanEditSurveyCustomerAddressGuard } from './guards/can-edit-survey-customer-address.guard';
import { GatewayConfigurationGuard } from './guards/gateway-configuration.guard';
import { MenuDisabledPageGuard } from './guards/menu-disabled-page.guard';
import { MenuEnabledPageGuard } from './guards/menu-enabled-page.guard';
import { QuoteEmailAddRecFileModalDeactivateGuard } from './guards/quote-email-add-rec-file-modal-deactivate.guard';
import { QuoteQuestionsDeactivateGuard } from './guards/quote-questions-deactivate.guard';
import { RecommendationAddGuard } from './guards/recommendation-add.guard';
import { RecommendationQuestionsDeactivateGuard } from './guards/recommendation-questions-deactivate.guard';
import { DashboardPageModule } from './pages/dashboard/dashboard.module';
import { QuoteEmailAddRecFileModalPageModule } from './pages/quote/quote-email-add-rec-file-modal/quote-email-add-rec-file-modal.module';
import { QuoteEmailAddRecFileModalPage } from './pages/quote/quote-email-add-rec-file-modal/quote-email-add-rec-file-modal.page';
import { QuoteQuestionsPageModule } from './pages/quote/quote-questions/quote-questions.module';
import { QuoteQuestionsPage } from './pages/quote/quote-questions/quote-questions.page';
import { RecommendationEditPageModule } from './pages/recommendation/recommendation-edit/recommendation-edit.module';
import { RecommendationQuestionsPageModule } from './pages/recommendation/recommendation-questions/recommendation-questions.module';
import { RecommendationQuestionsPage } from './pages/recommendation/recommendation-questions/recommendation-questions.page';
import { SearchResultsPageModule } from './pages/search-results/search-results/search-results.module';
import { SearchPageModule } from './pages/search/search/search.module';
import { CustomerLocationFieldRequirementsResolver } from './resolvers/customer-location-field-requirements/customer-location-field-requirements-resolver';
import { LaborRateGroupResolver } from './resolvers/labor-rate-group/labor-rate-group-resolver';
import { PhoneNumberFormatResolver } from './resolvers/phone-number-format/phone-number-format-resolver';
import { PriceMatrixResolver } from './resolvers/price-matrix/price-matrix-resolver';
import { QuoteStatusResolver } from './resolvers/quote-status/quote-status-resolver';
import { RecommendationStatusResolver } from './resolvers/recommendation-status/recommendation-status-resolver';
import { RecommendationUserDefinedStatusResolver } from './resolvers/recommendation-user-defined-status/recommendation-user-defined-status-resolver';
import { RegionResolver } from './resolvers/region/region-resolver';
import { TaxScheduleResolver } from './resolvers/tax-schedule/tax-schedule-resolver';
import { TechnicianResolver } from './resolvers/technician/technician-resolver';
import { QuoteUserDefinedStatusResolver } from './resolvers/quote-user-defined-status/quote-user-defined-status-resolver';
import { DashboardItemSettingsResolver } from './resolvers/dashboard/dashboard-item-settings/dashboard-item-settings-resolver';
import { IconResolver } from './resolvers/icon/icon-resolver';
import { UserDashboardItemsResolver } from './resolvers/dashboard/user-dashboard-items/user-dashboard-items-resolver';
import { DefaultDashboardItemsResolver } from './resolvers/dashboard/default-dashboard-items/default-dashboard-items-resolver';

const routes: Routes = [
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full'
  },
  {
    path: 'dashboard',
    canActivate: [AuthGuard, MenuEnabledPageGuard],
    loadChildren: () => import('./pages/dashboard/dashboard.module').then(m => m.DashboardPageModule)
  },
  {
    path: 'search',
    canActivate: [AuthGuard, MenuEnabledPageGuard],
    loadChildren: () => import('./pages/search/search/search.module').then(m => m.SearchPageModule),
    resolve: {
      recommendationStatus: RecommendationStatusResolver,
      recommendationUserDefinedStatus: RecommendationUserDefinedStatusResolver,
      quoteUserDefinedStatus: QuoteUserDefinedStatusResolver,
      quoteStatus: QuoteStatusResolver,
    }
  },
  {
    path: 'search-results',
    canActivate: [AuthGuard, MenuEnabledPageGuard],
    loadChildren: () => import('./pages/search-results/search-results/search-results.module').then(m => m.SearchResultsPageModule)
  },
  {
    path: 'login',
    canActivate: [MenuEnabledPageGuard, GatewayConfigurationGuard, AutoLoginGuard],
    loadChildren: () => import('./pages/user/login/login.module').then(m => m.LoginPageModule)
  },
  {
    path: 'gateway',
    canActivate: [MenuEnabledPageGuard, AutoLogoutGuard],
    loadChildren: () => import('./pages/user/gateway/gateway.module').then(m => m.GatewayPageModule)
  },
  {
    path: 'account',
    canActivate: [AuthGuard, MenuEnabledPageGuard],
    loadChildren: () => import('./pages/user/account/account.module').then(m => m.AccountPageModule)
  },
  {
    path: 'settings',
    children: [
      {
        path: '',
        canActivate: [AuthGuard, MenuEnabledPageGuard],
        loadChildren: () => import('./pages/settings/settings/settings.module').then(m => m.SettingsPageModule),
      },
      {
        path: 'user-dashboard',
        children: [
          {
            path: '',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/settings/settings-user-dashboard/settings-user-dashboard-list/settings-user-dashboard-list.module').then(m => m.SettingsUserDashboardListPageModule),
            resolve: {
              dashboardItems: UserDashboardItemsResolver
            }
          },{
            path: 'add',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/settings/settings-user-dashboard/settings-user-dashboard-add/settings-user-dashboard-add.module').then(m => m.SettingsUserDashboardAddPageModule),
          },
          {
            path: 'edit/:dashboardItemId',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/settings/settings-user-dashboard/settings-user-dashboard-edit/settings-user-dashboard-edit.module').then(m => m.SettingsUserDashboardEditPageModule),
          }
        ]
      },
      {
        path: 'default-dashboard',
        children: [
          {
            path: '',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/settings/settings-default-dashboard/settings-default-dashboard-list/settings-default-dashboard-list.module').then(m => m.SettingsDefaultDashboardListPageModule),
            resolve: {
              dashboardItems: DefaultDashboardItemsResolver
            }
          },{
            path: 'add',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/settings/settings-default-dashboard/settings-default-dashboard-add/settings-default-dashboard-add.module').then(m => m.SettingsDefaultDashboardAddPageModule),
          },
          {
            path: 'edit/:dashboardItemId',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/settings/settings-default-dashboard/settings-default-dashboard-edit/settings-default-dashboard-edit.module').then(m => m.SettingsDefaultDashboardEditPageModule),
          }
        ]
      }
    ]
  },
  {
    path: 'survey-prospect-customer-add',
    canActivate: [AuthGuard, MenuEnabledPageGuard],
    loadChildren: () => import('./pages/survey-prospect-customer/survey-prospect-customer-add/survey-prospect-customer-add.module').then(m => m.SurveyProspectCustomerAddPageModule),
    resolve: {
      phoneNumberFormat: PhoneNumberFormatResolver,
      laborRateGroup: LaborRateGroupResolver,
      priceMatrix: PriceMatrixResolver,
      taxSchedule: TaxScheduleResolver,
      technician: TechnicianResolver,
      customerLocationFieldRequirements: CustomerLocationFieldRequirementsResolver,
    }
  },
  {
    path: 'survey-prospect-customer-edit/:customerId',
    canActivate: [AuthGuard, MenuEnabledPageGuard],
    loadChildren: () => import('./pages/survey-prospect-customer/survey-prospect-customer-edit/survey-prospect-customer-edit.module').then(m => m.SurveyProspectCustomerEditPageModule)
  },
  {
    path: 'survey-prospect-customer-view/:customerId',
    canActivate: [AuthGuard, MenuEnabledPageGuard],
    loadChildren: () => import('./pages/survey-prospect-customer/survey-prospect-customer-view/survey-prospect-customer-view.module').then(m => m.SurveyProspectCustomerViewPageModule)
  },
  {
    path: 'survey-prospect-customer-address-add/:customerId',
    canActivate: [AuthGuard, MenuEnabledPageGuard],
    loadChildren: () => import('./pages/survey-prospect-customer-address/survey-prospect-customer-address-add/survey-prospect-customer-address-add.module').then(m => m.SurveyProspectCustomerAddressAddPageModule),
    resolve: {
      phoneNumberFormat: PhoneNumberFormatResolver,
      laborRateGroup: LaborRateGroupResolver,
      priceMatrix: PriceMatrixResolver,
      taxSchedule: TaxScheduleResolver,
      technician: TechnicianResolver,
      customerLocationFieldRequirements: CustomerLocationFieldRequirementsResolver,
    }
  },
  {
    path: 'survey-prospect-customer-address-details/:addressId',
    canActivate: [AuthGuard, MenuEnabledPageGuard],
    loadChildren: () => import('./pages/survey-prospect-customer-address/survey-prospect-customer-address-details/survey-prospect-customer-address-details.module').then(m => m.SurveyProspectCustomerAddressDetailsPageModule),
    resolve: {
      phoneNumberFormat: PhoneNumberFormatResolver
    }
  },
  {
    path: 'survey-prospect-customer-address-edit/:addressId',
    canActivate: [AuthGuard, MenuEnabledPageGuard, CanEditSurveyCustomerAddressGuard],
    loadChildren: () => import('./pages/survey-prospect-customer-address/survey-prospect-customer-address-edit/survey-prospect-customer-address-edit.module').then(m => m.SurveyProspectCustomerAddressEditPageModule),
    resolve: {
      phoneNumberFormat: PhoneNumberFormatResolver,
      laborRateGroup: LaborRateGroupResolver,
      priceMatrix: PriceMatrixResolver,
      taxSchedule: TaxScheduleResolver,
      technician: TechnicianResolver,
      customerLocationFieldRequirements: CustomerLocationFieldRequirementsResolver,
    }
  },
  {
    path: 'survey-prospect-customer-address-view',
    children: [
      {
        path: ':addressId',
        children: [
          {
            path: '',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/survey-prospect-customer-address/survey-prospect-customer-address-view/survey-prospect-customer-address-view.module').then(m => m.SurveyProspectCustomerAddressViewPageModule)
          },
          {
            path: 'survey-equipments',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/survey-equipment/survey-equipment-list/survey-equipment-list.module').then(m => m.SurveyEquipmentListPageModule)
          },
          {
            path: 'survey-equipment',
            children: [
              {
                path: '',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/survey-equipment/survey-equipment-add/survey-equipment-add.module').then(m => m.SurveyEquipmentAddPageModule)
              },
              {
                path: 'file-view/:fileId',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/survey-equipment-files/survey-equipment-file-view/survey-equipment-file-view.module').then(m => m.SurveyEquipmentFileViewPageModule)
              },
              {
                path: 'file-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/survey-equipment-files/survey-equipment-file-upload/survey-equipment-file-upload.module').then(m => m.SurveyEquipmentFileUploadPageModule)
              },
              {
                path: 'image-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/survey-equipment-files/survey-equipment-file-image-upload/survey-equipment-file-image-upload.module').then(m => m.SurveyEquipmentFileImageUploadPageModule)
              },
              {
                path: 'audio-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/survey-equipment-files/survey-equipment-file-audio-upload/survey-equipment-file-audio-upload.module').then(m => m.SurveyEquipmentFileAudioUploadPageModule)
              },
            ],
          },
          {
            path: 'survey-equipment/:equipmentId',
            children: [
              {
                path: '',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/survey-equipment/survey-equipment-edit/survey-equipment-edit.module').then(m => m.SurveyEquipmentEditPageModule)
              },
              {
                path: 'file-view/:fileId',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/survey-equipment-files/survey-equipment-file-view/survey-equipment-file-view.module').then(m => m.SurveyEquipmentFileViewPageModule)
              },
              {
                path: 'file-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/survey-equipment-files/survey-equipment-file-upload/survey-equipment-file-upload.module').then(m => m.SurveyEquipmentFileUploadPageModule)
              },
              {
                path: 'image-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/survey-equipment-files/survey-equipment-file-image-upload/survey-equipment-file-image-upload.module').then(m => m.SurveyEquipmentFileImageUploadPageModule)
              },
              {
                path: 'audio-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/survey-equipment-files/survey-equipment-file-audio-upload/survey-equipment-file-audio-upload.module').then(m => m.SurveyEquipmentFileAudioUploadPageModule)
              },
            ],
          },
        ]
      },
    ]
  },
  {
    path: 'customer-add',
    canActivate: [AuthGuard, MenuEnabledPageGuard],
    loadChildren: () => import('./pages/customer/customer-add/customer-add.module').then(m => m.CustomerAddPageModule),
    resolve: {
      phoneNumberFormat: PhoneNumberFormatResolver,
      laborRateGroup: LaborRateGroupResolver,
      priceMatrix: PriceMatrixResolver,
      taxSchedule: TaxScheduleResolver,
      customerLocationFieldRequirements: CustomerLocationFieldRequirementsResolver
    }
  },
  {
    path: 'customer-view/:type/:customerId',
    canActivate: [AuthGuard, MenuEnabledPageGuard],
    loadChildren: () => import('./pages/customer/customer-view/customer-view.module').then(m => m.CustomerViewPageModule)
  },
  {
    path: 'customer-edit/:type/:customerId',
    canActivate: [AuthGuard, MenuEnabledPageGuard],
    loadChildren: () => import('./pages/customer/customer-edit/customer-edit.module').then(m => m.CustomerEditPageModule)
  },
  {
    path: 'customer-address-add/:type/:customerId',
    canActivate: [AuthGuard, MenuEnabledPageGuard],
    loadChildren: () => import('./pages/customer-address/customer-address-add/customer-address-add.module').then(m => m.CustomerAddressAddPageModule),
    resolve: {
      phoneNumberFormat: PhoneNumberFormatResolver,
      laborRateGroup: LaborRateGroupResolver,
      priceMatrix: PriceMatrixResolver,
      taxSchedule: TaxScheduleResolver,
      customerLocationFieldRequirements: CustomerLocationFieldRequirementsResolver
    }
  },
  {
    path: 'customer-address-view',
    children: [
      {
        path: ':type/:addressId',
        children: [
          {
            path: '',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/customer-address/customer-address-view/customer-address-view.module').then(m => m.CustomerAddressViewPageModule)
          },
          {
            path: 'equipments',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/equipment/equipment-list/equipment-list.module').then(m => m.EquipmentListPageModule)
          },
          {
            path: 'equipment',
            children: [
              {
                path: '',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/equipment/equipment-add/equipment-add.module').then(m => m.EquipmentAddPageModule)
              },
              {
                path: 'file-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/equipment-files/equipment-file-upload/equipment-file-upload.module').then(m => m.EquipmentFileUploadPageModule)
              },
              {
                path: 'audio-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/equipment-files/equipment-file-audio-upload/equipment-file-audio-upload.module').then(m => m.EquipmentFileAudioUploadPageModule)
              },
              {
                path: 'image-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/equipment-files/equipment-file-image-upload/equipment-file-image-upload.module').then(m => m.EquipmentFileImageUploadPageModule)
              },
              {
                path: 'file-view/:fileId',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/equipment-files/equipment-file-view/equipment-file-view.module').then(m => m.EquipmentFileViewPageModule)
              },
            ]
          },
          {
            path: 'equipment/:equipmentId',
            children: [
              {
                path: '',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/equipment/equipment-edit/equipment-edit.module').then(m => m.EquipmentEditPageModule),
              },
              {
                path: 'file-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/equipment-files/equipment-file-upload/equipment-file-upload.module').then(m => m.EquipmentFileUploadPageModule)
              },
              {
                path: 'audio-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/equipment-files/equipment-file-audio-upload/equipment-file-audio-upload.module').then(m => m.EquipmentFileAudioUploadPageModule)
              },
              {
                path: 'image-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/equipment-files/equipment-file-image-upload/equipment-file-image-upload.module').then(m => m.EquipmentFileImageUploadPageModule)
              },
              {
                path: 'file-view/:fileId',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/equipment-files/equipment-file-view/equipment-file-view.module').then(m => m.EquipmentFileViewPageModule)
              },
            ]
          },
          {
            path: 'survey-equipments',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/survey-equipment/survey-equipment-list/survey-equipment-list.module').then(m => m.SurveyEquipmentListPageModule)
          },
          {
            path: 'survey-equipment',
            children: [
              {
                path: '',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/survey-equipment/survey-equipment-add/survey-equipment-add.module').then(m => m.SurveyEquipmentAddPageModule)
              },
              {
                path: 'file-view/:fileId',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/survey-equipment-files/survey-equipment-file-view/survey-equipment-file-view.module').then(m => m.SurveyEquipmentFileViewPageModule)
              },
              {
                path: 'file-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/survey-equipment-files/survey-equipment-file-upload/survey-equipment-file-upload.module').then(m => m.SurveyEquipmentFileUploadPageModule)
              },
              {
                path: 'image-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/survey-equipment-files/survey-equipment-file-image-upload/survey-equipment-file-image-upload.module').then(m => m.SurveyEquipmentFileImageUploadPageModule)
              },
              {
                path: 'audio-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/survey-equipment-files/survey-equipment-file-audio-upload/survey-equipment-file-audio-upload.module').then(m => m.SurveyEquipmentFileAudioUploadPageModule)
              },
            ],
          },
          {
            path: 'survey-equipment/:equipmentId',
            children: [
              {
                path: '',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/survey-equipment/survey-equipment-edit/survey-equipment-edit.module').then(m => m.SurveyEquipmentEditPageModule)
              },
              {
                path: 'file-view/:fileId',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/survey-equipment-files/survey-equipment-file-view/survey-equipment-file-view.module').then(m => m.SurveyEquipmentFileViewPageModule)
              },
              {
                path: 'file-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/survey-equipment-files/survey-equipment-file-upload/survey-equipment-file-upload.module').then(m => m.SurveyEquipmentFileUploadPageModule)
              },
              {
                path: 'image-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/survey-equipment-files/survey-equipment-file-image-upload/survey-equipment-file-image-upload.module').then(m => m.SurveyEquipmentFileImageUploadPageModule)
              },
              {
                path: 'audio-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/survey-equipment-files/survey-equipment-file-audio-upload/survey-equipment-file-audio-upload.module').then(m => m.SurveyEquipmentFileAudioUploadPageModule)
              },
            ],
          },
          {
            path: 'quotes',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/quote/quote-list/quote-list.module').then(m => m.QuoteListPageModule)
          },
          {
            path: 'recommendations',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/recommendation/recommendation-list/recommendation-list.module').then(m => m.RecommendationListPageModule)
          },
        ]
      }
    ]
  },
  {
    path: 'customer-address-edit/:type/:addressId',
    canActivate: [AuthGuard, MenuEnabledPageGuard],
    loadChildren: () => import('./pages/customer-address/customer-address-edit/customer-address-edit.module').then(m => m.CustomerAddressEditPageModule),
    resolve: {
      phoneNumberFormat: PhoneNumberFormatResolver,
      laborRateGroup: LaborRateGroupResolver,
      priceMatrix: PriceMatrixResolver,
      taxSchedule: TaxScheduleResolver,
      customerLocationFieldRequirements: CustomerLocationFieldRequirementsResolver
    }
  },
  {
    path: 'customer-address-details/:type/:addressId',
    canActivate: [AuthGuard, MenuEnabledPageGuard],
    loadChildren: () => import('./pages/customer-address/customer-address-details/customer-address-details.module').then(m => m.CustomerAddressDetailsPageModule),
    resolve: {
      phoneNumberFormat: PhoneNumberFormatResolver
    }
  },
  {
    path: 'select-customer/:model',
    canActivate: [AuthGuard, MenuEnabledPageGuard],
    loadChildren: () => import('./pages/generic-add-step-one/generic-add-step-one.module').then(m => m.GenericAddStepOnePageModule)
  },
  {
    path: 'select-location/:model',
    canActivate: [AuthGuard, MenuEnabledPageGuard],
    loadChildren: () => import('./pages/generic-add-step-two/generic-add-step-two.module').then(m => m.GenericAddStepTwoPageModule)
  },
  {
    path: 'recommendation',
    canActivate: [AuthGuard, MenuEnabledPageGuard, RecommendationAddGuard],
    loadChildren: () => import('./pages/recommendation/recommendation-add/recommendation-add.module').then(m => m.RecommendationAddPageModule)
  },
  {
    path: 'recommendation/:recommendationId',
    children: [
      {
        path: '',
        canActivate: [AuthGuard, MenuEnabledPageGuard, /*RecommendationEditGuard*/],
        loadChildren: () => import('./pages/recommendation/recommendation-edit/recommendation-edit.module').then(m => m.RecommendationEditPageModule),
        resolve: {
          regions: RegionResolver
        }
      },
      {
        path: 'copy',
        children: [
          {
            path: '',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/recommendation/recommendation-copy/recommendation-copy.module').then(m => m.RecommendationCopyPageModule)
          },
          {
            path: 'item-replace-list',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/recommendation/recommendation-copy-item-replace-list/recommendation-copy-item-replace-list.module').then(m => m.RecommendationCopyItemReplaceListPageModule)
          },
          {
            path: 'item-update-list',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/recommendation/recommendation-copy-item-update-list/recommendation-copy-item-update-list.module').then(m => m.RecommendationCopyItemUpdateListPageModule)
          },
          {
            path: 'replace-item',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/recommendation/recommendation-copy-replace-item/recommendation-copy-replace-item.module').then(m => m.RecommendationCopyReplaceItemPageModule),
            resolve: {
              regions: RegionResolver
            }
          },
        ]
      },
      {
        path: 'equipment',
        children: [
          {
            path: '',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/recommendation/recommendation-equipment-add/recommendation-equipment-add.module').then(m => m.RecommendationEquipmentAddPageModule)
          },
          {
            path: 'file-upload',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/equipment-files/equipment-file-upload/equipment-file-upload.module').then(m => m.EquipmentFileUploadPageModule)
          },
          {
            path: 'audio-upload',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/equipment-files/equipment-file-audio-upload/equipment-file-audio-upload.module').then(m => m.EquipmentFileAudioUploadPageModule)
          },
          {
            path: 'image-upload',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/equipment-files/equipment-file-image-upload/equipment-file-image-upload.module').then(m => m.EquipmentFileImageUploadPageModule)
          },
          {
            path: 'file-view/:fileId',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/equipment-files/equipment-file-view/equipment-file-view.module').then(m => m.EquipmentFileViewPageModule)
          },

        ],
      },
      {
        path: 'equipment',
        children: [
          {
            path: ':equipmentId',
            children: [
              {
                path: '',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/recommendation/recommendation-equipment-edit/recommendation-equipment-edit.module').then(m => m.RecommendationEquipmentEditPageModule),
              },
              {
                path: 'file-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/equipment-files/equipment-file-upload/equipment-file-upload.module').then(m => m.EquipmentFileUploadPageModule)
              },
              {
                path: 'audio-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/equipment-files/equipment-file-audio-upload/equipment-file-audio-upload.module').then(m => m.EquipmentFileAudioUploadPageModule)
              },
              {
                path: 'image-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/equipment-files/equipment-file-image-upload/equipment-file-image-upload.module').then(m => m.EquipmentFileImageUploadPageModule)
              },
              {
                path: 'file-view/:fileId',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/equipment-files/equipment-file-view/equipment-file-view.module').then(m => m.EquipmentFileViewPageModule)
              },

            ]
          }
        ]
      },
      {
        path: 'equipment-list',
        canActivate: [AuthGuard, MenuEnabledPageGuard],
        loadChildren: () => import('./pages/recommendation/recommendation-equipment-list/recommendation-equipment-list.module').then(m => m.RecommendationEquipmentListPageModule)
      },
      {
        path: 'equipment-view',
        children: [
          {
            path: ':equipmentId',
            children: [
              {
                path: '',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/recommendation/recommendation-equipment-view/recommendation-equipment-view.module').then(m => m.RecommendationEquipmentViewPageModule)
              },
              {
                path: 'file-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/equipment-files/equipment-file-upload/equipment-file-upload.module').then(m => m.EquipmentFileUploadPageModule)
              },
              {
                path: 'audio-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/equipment-files/equipment-file-audio-upload/equipment-file-audio-upload.module').then(m => m.EquipmentFileAudioUploadPageModule)
              },
              {
                path: 'image-upload',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/equipment-files/equipment-file-image-upload/equipment-file-image-upload.module').then(m => m.EquipmentFileImageUploadPageModule)
              },
              {
                path: 'file-view/:fileId',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/equipment-files/equipment-file-view/equipment-file-view.module').then(m => m.EquipmentFileViewPageModule)
              },
            ]
          }
        ]
      },
      {
        path: 'equipment-review',
        children: [
          {
            path: ':equipmentId',
            children: [
              {
                path: '',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/recommendation/recommendation-equipment-review/recommendation-equipment-review.module').then(m => m.RecommendationEquipmentReviewPageModule)
              },
              {
                path: 'file-view/:fileId',
                canActivate: [AuthGuard, MenuEnabledPageGuard],
                loadChildren: () => import('./pages/equipment-files/equipment-file-view/equipment-file-view.module').then(m => m.EquipmentFileViewPageModule)
              },
            ]
          }
        ]
      },
      {
        path: 'items',
        canActivate: [AuthGuard, MenuEnabledPageGuard],
        loadChildren: () => import('./pages/recommendation/recommendation-item-list/recommendation-item-list.module').then(m => m.RecommendationItemListPageModule)
      },
      {
        path: 'item-type',
        canActivate: [AuthGuard, MenuEnabledPageGuard],
        loadChildren: () => import('./pages/recommendation/recommendation-item-app-type-select/recommendation-item-app-type-select.module').then(m => m.RecommendationItemAppTypeSelectPageModule)
      },
      {
        path: 'item',
        children: [
          {
            path: '',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/recommendation/recommendation-item-add/recommendation-item-add.module').then(m => m.RecommendationItemAddPageModule)
          },
          {
            path: 'questions',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/recommendation/recommendation-item-questions/recommendation-item-questions.module').then(m => m.RecommendationItemQuestionsPageModule)
          },
        ],
      },
      {
        path: 'labor',
        canActivate: [AuthGuard, MenuEnabledPageGuard],
        loadChildren: () => import('./pages/recommendation/recommendation-item-add-service-labor/recommendation-item-add-service-labor.module').then(m => m.RecommendationItemAddServiceLaborPageModule),
        resolve: {
          regions: RegionResolver
        }
      },
      {
        path: 'assembly',
        children: [
          {
            path: '',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/recommendation/recommendation-item-add-assembly/recommendation-item-add-assembly.module').then(m => m.RecommendationItemAddAssemblyPageModule),
            resolve: {
              regions: RegionResolver
            }
          },
          {
            path: 'items',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/recommendation/recommendation-item-add-assembly-items/recommendation-item-add-assembly-items.module').then(m => m.RecommendationItemAddAssemblyItemsPageModule)
          },
          {
            path: 'questions',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/recommendation/recommendation-item-questions/recommendation-item-questions.module').then(m => m.RecommendationItemQuestionsPageModule)
          },
        ]
      },
      {
        path: 'item/:id',
        children: [
          {
            path: '',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/recommendation/recommendation-item-edit/recommendation-item-edit.module').then(m => m.RecommendationItemEditPageModule)
          },
          {
            path: 'questions',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/recommendation/recommendation-item-questions-edit/recommendation-item-questions-edit.module').then(m => m.RecommendationItemQuestionsEditPageModule)
          },
        ],
      },
      {
        path: 'questions',
        canActivate: [AuthGuard, MenuEnabledPageGuard],
        canDeactivate: [RecommendationQuestionsDeactivateGuard],
        component: RecommendationQuestionsPage // The deactivation guard does not work with page module lazy loading
        // loadChildren: () => import('./pages/recommendation/recommendation-questions/recommendation-questions.module').then(m => m.RecommendationQuestionsPageModule)
      },
      {
        path: 'image-upload',
        canActivate: [AuthGuard, MenuEnabledPageGuard],
        loadChildren: () => import('./pages/recommendation/recommendation-file-image-upload/recommendation-file-image-upload.module').then(m => m.RecommendationFileImageUploadPageModule)
      },
      {
        path: 'audio-upload',
        canActivate: [AuthGuard, MenuEnabledPageGuard],
        loadChildren: () => import('./pages/recommendation/recommendation-file-audio-upload/recommendation-file-audio-upload.module').then(m => m.RecommendationFileAudioUploadPageModule)
      },
      {
        path: 'file-upload',
        canActivate: [AuthGuard, MenuEnabledPageGuard],
        loadChildren: () => import('./pages/recommendation/recommendation-file-upload/recommendation-file-upload.module').then(m => m.RecommendationFileUploadPageModule)
      },
      {
        path: 'file-view/:fileId',
        canActivate: [AuthGuard, MenuEnabledPageGuard],
        loadChildren: () => import('./pages/recommendation/recommendation-file-view/recommendation-file-view.module').then(m => m.RecommendationFileViewPageModule)
      },

    ]
  },
  {
    path: 'quote',
    canActivate: [AuthGuard, MenuEnabledPageGuard],
    loadChildren: () => import('./pages/quote/quote-add/quote-add.module').then(m => m.QuoteAddPageModule)
  },
  {
    path: 'quote/:quoteId',
    children: [
      {
        path: '',
        canActivate: [AuthGuard, MenuEnabledPageGuard, /*QuoteEditGuard*/],
        loadChildren: () => import('./pages/quote/quote-edit/quote-edit.module').then(m => m.QuoteEditPageModule)
      },
      {
        path: 'recommendations',
        canActivate: [AuthGuard, MenuEnabledPageGuard],
        loadChildren: () => import('./pages/quote/quote-edit-recommendation/quote-edit-recommendation.module').then(m => m.QuoteEditRecommendationPageModule)
      },
      {
        path: 'items',
        canActivate: [AuthGuard, MenuEnabledPageGuard],
        loadChildren: () => import('./pages/quote/quote-items-list/quote-items-list.module').then(m => m.QuoteItemsListPageModule)
      },
      {
        path: 'item-type',
        canActivate: [AuthGuard, MenuEnabledPageGuard],
        loadChildren: () => import('./pages/quote/quote-item-app-type-select/quote-item-app-type-select.module').then(m => m.QuoteItemAppTypeSelectPageModule)
      },
      {
        path: 'item',
        children: [
          {
            path: '',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/quote/quote-item-add/quote-item-add.module').then(m => m.QuoteItemAddPageModule)
          },
          {
            path: 'questions',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/quote/quote-item-questions/quote-item-questions.module').then(m => m.QuoteItemQuestionsPageModule)
          },
        ]
      },
      {
        path: 'labor',
        canActivate: [AuthGuard, MenuEnabledPageGuard],
        loadChildren: () => import('./pages/quote/quote-item-add-service-labor/quote-item-add-service-labor.module').then(m => m.QuoteItemAddServiceLaborPageModule),
        resolve: {
          regions: RegionResolver
        }
      },
      {
        path: 'assembly',
        children: [
          {
            path: '',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/quote/quote-item-add-assembly/quote-item-add-assembly.module').then(m => m.QuoteItemAddAssemblyPageModule),
            resolve: {
              regions: RegionResolver
            }
          },
          {
            path: 'items',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/quote/quote-item-add-assembly-items/quote-item-add-assembly-items.module').then(m => m.QuoteItemAddAssemblyItemsPageModule)
          },
          {
            path: 'questions',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/quote/quote-item-questions/quote-item-questions.module').then(m => m.QuoteItemQuestionsPageModule)
          },
        ]
      },
      {
        path: 'items/:id',
        children: [
          {
            path: '',
            loadChildren: () => import('./pages/quote/quote-item-edit/quote-item-edit.module').then(m => m.QuoteItemEditPageModule)
          },
          {
            path: 'questions',
            loadChildren: () => import('./pages/quote/quote-item-questions-edit/quote-item-questions-edit.module').then(m => m.QuoteItemQuestionsEditPageModule)
          },
        ]
      },
      {
        path: 'markup-adjustment',
        canActivate: [AuthGuard, MenuEnabledPageGuard],
        loadChildren: () => import('./pages/quote/quote-markup-adjustment/quote-markup-adjustment.module').then(m => m.QuoteMarkupAdjustmentPageModule)
      },
      {
        path: 'questions',
        canActivate: [AuthGuard, MenuEnabledPageGuard],
        canDeactivate: [QuoteQuestionsDeactivateGuard],
        component: QuoteQuestionsPage,
        resolve: {
          phoneNumberFormat: PhoneNumberFormatResolver,
        }
        // loadChildren: () => import('./pages/quote/quote-questions/quote-questions.module').then( m => m.QuoteQuestionsPageModule)
      },
      {
        path: 'email',
        children: [
          {
            path: '',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/quote/quote-email-preview/quote-email-preview.module').then(m => m.QuoteEmailPreviewPageModule)
          },
          {
            path: 'attachments',
            canActivate: [AuthGuard, MenuDisabledPageGuard],
            canDeactivate: [QuoteEmailAddRecFileModalDeactivateGuard],
            component: QuoteEmailAddRecFileModalPage
            // loadChildren: () => import('./pages/quote/quote-email-add-rec-file-modal/quote-email-add-rec-file-modal.module').then( m => m.QuoteEmailAddRecFileModalPageModule)
          },
        ]
      },
      {
        path: 'approval',
        canActivate: [AuthGuard, MenuDisabledPageGuard],
        loadChildren: () => import('./pages/quote/quote-customer-approval/quote-customer-approval.module').then(m => m.QuoteCustomerApprovalPageModule)
      },
      {
        path: 'approval-view',
        canActivate: [AuthGuard, MenuEnabledPageGuard],
        loadChildren: () => import('./pages/quote/quote-customer-approval-view/quote-customer-approval-view.module').then(m => m.QuoteCustomerApprovalViewPageModule)
      },
      {
        path: 'service-call',
        canActivate: [AuthGuard, MenuEnabledPageGuard],
        loadChildren: () => import('./pages/quote/quote-service-call/quote-service-call.module').then(m => m.QuoteServiceCallPageModule),
        resolve: {
          phoneNumberFormat: PhoneNumberFormatResolver
        }
      },
      {
        path: 'existing-service-call',
        canActivate: [AuthGuard, MenuEnabledPageGuard],
        loadChildren: () => import('./pages/quote/confirm-add-to-existing-service-call-view/confirm-add-to-existing-service-call-view.module').then(m => m.ConfirmAddToExistingServiceCallViewPageModule)
      },

      {
        path: 'copy',
        children: [
          {
            path: '',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/quote/quote-copy/quote-copy.module').then(m => m.QuoteCopyPageModule)
          },
          {
            path: 'item-replace-list',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/quote/quote-copy-item-replace-list/quote-copy-item-replace-list.module').then(m => m.QuoteCopyItemReplaceListPageModule)
          },
          {
            path: 'item-update-list',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/quote/quote-copy-item-update-list/quote-copy-item-update-list.module').then(m => m.QuoteCopyItemUpdateListPageModule)
          },
          {
            path: 'replace-item',
            canActivate: [AuthGuard, MenuEnabledPageGuard],
            loadChildren: () => import('./pages/quote/quote-copy-replace-item/quote-copy-replace-item.module').then(m => m.QuoteCopyReplaceItemPageModule),
            resolve: {
              regions: RegionResolver
            }
          },
        ]
      },
    ]
  },
  {
    path: '**', // 404 route
    redirectTo: 'dashboard'
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules, useHash: true })
  ],
  exports: [RouterModule],
  providers: [
    PhoneNumberFormatResolver,
    RecommendationStatusResolver,
    RecommendationUserDefinedStatusResolver,
    QuoteUserDefinedStatusResolver,
    QuoteStatusResolver,
    LaborRateGroupResolver,
    PriceMatrixResolver,
    TaxScheduleResolver,
    CustomerLocationFieldRequirementsResolver,
    TechnicianResolver,
    RegionResolver,
    DashboardItemSettingsResolver,
    IconResolver,
    UserDashboardItemsResolver,
    DefaultDashboardItemsResolver,
    DashboardPageModule,
    SearchPageModule,
    SearchResultsPageModule,
    RecommendationEditPageModule,
    RecommendationQuestionsPageModule,
    QuoteQuestionsPageModule,
    QuoteEmailAddRecFileModalPageModule,
  ]
})
export class AppRoutingModule { }
